import { useMemo } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { DashboardPage } from '~/components/ui/dashboard/DashboardPage';
import { canAccess } from '~/utils/rbac';
import { useEnsureDashboard } from '~/hooks/useEnsureDashboard';
import { ErrorComponent } from '~/routes/__root';
import { portfolioIdAtom } from '~/atoms/dashboardAtom';
import { DEFAULT_DASHBOARD_LAYOUT_SL } from '~/constants/constants';
import type { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { usePortfolios } from '~/hooks/usePortfolios';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

// Define which widgets are allowed on the portfolio settings page
const ALLOWED_PFOLIO_SETTINGS_WIDGETS = [
  'allocation',
  'allocation-table',
] as const;

/**
 * Creates initial widget configs for portfolio settings dashboard by filtering default layout
 * and generating unique IDs for each widget
 */
const createPfolioSettingsDashboardConfig = (): Config[] => {
  return DEFAULT_DASHBOARD_LAYOUT_SL.filter((widget) =>
    ALLOWED_PFOLIO_SETTINGS_WIDGETS.some((allowedWidget) =>
      widget.id.includes(allowedWidget),
    ),
  ).map((widget) => ({
    ...widget,
    id: `${widget.id}-widget-${crypto.randomUUID()}`,
  }));
};

// Removed unused local Dashboard type

function PfolioSettingsComponent() {
  // Get portfolio ID from atom or fetch first available portfolio
  const currentPortfolioId = useAtomValue(portfolioIdAtom);
  const { portfolios } = usePortfolios();

  const portfolioId = currentPortfolioId || portfolios[0]?.id || '';

  const initialPfolioSettingsConfig = useMemo(
    () => createPfolioSettingsDashboardConfig(),
    [],
  );

  const { dashboardId, isLoading, error } = useEnsureDashboard({
    type: 'pfolio-settings',
    name: 'Portfolio Settings Dashboard',
    initialConfig: initialPfolioSettingsConfig,
    portfolioId,
  });

  if (error) {
    return <ErrorComponent />;
  }

  if (isLoading || !dashboardId) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen">
        <div className="text-center">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <DashboardPage
      dashboardId={dashboardId}
      breadcrumbLabel="Portfolio Settings"
      breadcrumbUrl="/pfolio-settings"
      pageTaglinePrefix="Portfolio Settings"
      allowedWidgets={ALLOWED_PFOLIO_SETTINGS_WIDGETS}
      emptyStateTitle="No Portfolio Settings Widgets Found"
      emptyStateMessage="This dashboard doesn't have any portfolio settings widgets yet. Add some widgets to get started."
      emptyStateButtonLabel="Add Widget"
    />
  );
}

const PfolioSettingsRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);
  if (!allowed) return null;
  return <PfolioSettingsComponent />;
};

export const Route = createFileRoute('/_authLayout/pfolio-settings')({
  beforeLoad: async () => {
    if (!canAccess('dashboard')) {
      throw notFound();
    }
  },
  component: PfolioSettingsRoute,
});
